/**
 * Ponowne liczenie audytu z wyniku trzymanego w pamięci (zmiana języka, zmiana reguł,
 * otwarcie raportu z dashboardu).
 *
 * Do 27.09.2026 każde takie miejsce wołało runAudit z `previousYear = null` i `txError = null`.
 * Znikały wtedy reguły oparte na roku poprzednim (ciągłość IB/UB, porównania r/r, rok nie
 * domknięty) i powód pominięcia reguł transakcyjnych, a ocena rosła bez zmiany ksiąg.
 *
 * Dane roku poprzedniego żyją tylko w pamięci przeglądarki. Zapis na serwer wybiera pola
 * jawnie (syncBatchResultsToServer), więc historia audytów przez to nie rośnie.
 */
import { runAudit, getCurrentLang, setCurrentLang } from '@engine/index';

// Raport z serwera nie ma sald, więc nie da się go przeliczyć w języku widoku. Bieg zapisuje
// teksty kart także w drugim języku (15.10.2026, Artur: norweskie opisy w polskim widoku).
const ISSUE_TEXT_FIELDS = ['title', 'desc', 'action', 'why', 'verified'];
export const attachIssueTranslations = (issues, lang, run) => {
    if (!Array.isArray(issues) || lang === getCurrentLang()) return issues;
    const prev = getCurrentLang();
    let other;
    try { setCurrentLang(lang); other = run()?.issues; } catch { return issues; } finally { setCurrentLang(prev); }
    // Tylko zgodność 1:1; inna kolejność albo liczba kart = zostaje stare zachowanie.
    if (!Array.isArray(other) || other.length !== issues.length
        || other.some((o, i) => o.ruleId !== issues[i].ruleId || o.specialType !== issues[i].specialType || o.type !== issues[i].type)) return issues;
    return issues.map((issue, i) => ({ ...issue, tr: { ...issue.tr,
        [lang]: Object.fromEntries(ISSUE_TEXT_FIELDS.filter(f => other[i][f] !== undefined).map(f => [f, other[i][f]])) } }));
};
export const otherLang = () => getCurrentLang() === 'pl' ? 'no' : 'pl';

// Bieg przez API liczy każdą firmę jako '24seven', także z Uni Micro, bo RAPPORTER oddaje oba
// źródła w tym samym kształcie. Przeliczenie musi użyć tego samego systemu: 'unimicro' wyłącza
// reguły przypisane do 24SO (np. mva_period_check) i zmienia ocenę bez zmiany ksiąg.
export const API_AUDIT_SYSTEM = '24seven';
export const reopenEngineSystem = firm => firm.engineSystem || API_AUDIT_SYSTEM;

export const storedAuditInput = (firm, disabledRules, auditMeta = firm.auditMeta || null) => ({
    system: reopenEngineSystem(firm),
    sourceLabel: ['24seven','24SevenOffice','24so',''].includes(firm.system || '') ? 'Finago' : firm.system,
    saldo: firm.saldo || [],
    resultat: firm.resultat || [],
    disabledRules,
    previousYear: firm.previousYear || null,
    vatMonthly: firm.vatMonthly || [],
    transactions: firm.transactions || [],
    txError: firm.txError || null,
    auditMeta: {...auditMeta,apiSourceProofRequired: !firm.engineSystem || firm.engineSystem===API_AUDIT_SYSTEM},
});

export const rerunStoredAudit = (firm, disabledRules, auditMeta) => {
    const i = storedAuditInput(firm, disabledRules, auditMeta);
    return runAudit(i.system, i.saldo, i.resultat, i.disabledRules, i.previousYear, i.vatMonthly, i.transactions, i.txError, i.auditMeta);
};

/**
 * Dane wejściowe przypięte do KONKRETNEGO obiektu raportu. Ekrany podstawiają raport także
 * bezpośrednio (setReport z kontekstu), więc zgodność sprawdzamy po tożsamości obiektu:
 * inny obiekt na ekranie znaczy inny raport, nawet gdy treść jest taka sama.
 */
export const bindToReport = (input, report) => (input ? { ...input, report } : null);

/**
 * Czy wolno przeliczyć raport, który jest na ekranie. Nie wolno, gdy zapamiętane dane należą
 * do innego raportu (po otwarciu firmy B zostawały dane firmy A) albo gdy nie ma sald
 * (raport z historii serwera przeliczony z pustych danych dawał 10,0 i zero znalezisk).
 */
export const canRerun = (input, report) =>
    !!input && !!report && input.report === report && input.saldo.length > 0;

// Legacy snapshots did not store all inputs. Keep their report instead of silently
// dropping monthly, previous-year and transaction checks during a reload.
export const restoreLastAudit = (saved, disabledRules) => {
    if (!saved.auditInput) return { report: saved.report, input: null, period: saved.period || null };
    const input = { ...saved.auditInput, disabledRules, auditMeta:{...saved.auditInput.auditMeta,apiSourceProofRequired:saved.auditInput.system===API_AUDIT_SYSTEM || saved.auditInput.auditMeta?.apiSourceProofRequired} };
    return {
        report: { ...runAudit(input.system, input.saldo, input.resultat, disabledRules,
            input.previousYear || null, input.vatMonthly || [], input.transactions || [],
            input.txError || null, input.auditMeta || null), auditedAt: saved.report?.auditedAt ?? null },
        input, period: saved.period || null,
    };
};
